import type { Landmark } from '../../../../data/types';
import type { Ring, Vec2 } from '../../../../geo/types';
import { dominantAxis, planFrameOf, type PlanFrame } from '../planFrame';
import type { ChapelPlan, Side } from '../churchModel';

const DEG = Math.PI / 180;

/** The Amsterdam foot in metres, in which Janse quotes the lantern's measure. */
export const AMSTERDAM_FOOT = 0.2831;

/**
 * The Oude Kerk as it stood in 1538 (docs/plans/landmarks.md, "Oude Kerk"), in the plan frame of its
 * traced footprint: u east along the axis, v north. Places are measured on that footprint (its
 * vertex numbers are given in the doc); sizes and dates are Janse 2004 unless stated.
 */
export const OUDE_KERK = {
  /* The west front of the hall nave: footprint vertices 4, 7, 8, 13 and 14. */
  westU: -30.4,
  /* The tower of c.1325, 9.06 m square with a stone body about 28 m high, on the axis of the middle
     vessel. Its needle of c.1510 is drawn by the painting (scan 3035-3100 x 2555-2715) and the 1544
     woodcut (uva 1775-1835 x 2255-2385) with an open lantern high up under a bulbous cap: the spire
     is 0.627 of the body in the painting (frame px 767.5-807 over 807-870); the lantern's foot stands
     at 0.64 (woodcut) to 0.73 (painting) of the spire, and the lantern is 10 by 7 feet like the
     lantern of the Zwolle Peperbus Janse compares it with (pp. 134-135). */
  tower: {
    side: 9.06,
    bodyY: 28,
    overlap: 0.2,
    vc: 0.93,
    lanternFoot: 40.0,
    lanternTop: 40.0 + 10 * AMSTERDAM_FOOT,
    tipY: 45.55,
    lanternWidth: 7 * AMSTERDAM_FOOT,
  },
  /* The hall nave of c.1390: eight bays from the west front to the choir, their module fitted to the
     traced length (5.2 m; Janse gives about 4.90 m inside the walls), a middle vessel over 10 m wide
     and aisles of about 11 m between the arcade walls. */
  nave: { bays: 8, half: 5.2, aisle: 11, eaveY: 14, pitch: 55 * DEG },
  /* The clerestory of 1510-12 over the six western bays: a long roof whose ridge stands about 4.2 m over
     the old middle ridge, cut on every bay by a transverse roof of equal height ending in a gable of 70
     degrees with a four-light lancet whose sill is about 2.76 m over the walkway at the old roof foot.
     The two eastern bays, the crossing, stayed low; a timber partition closed the raised nave there
     (Janse 2004, section 1.11; woodcut 1544). The window's width and head are not recorded. */
  lichtbeuk: { bays: 6, rise: 4.2, gablePitch: 70 * DEG, sillOverWalkway: 2.76, window: { width: 2.2, topBelowRidge: 1.5 } },
  /* The chapels between the buttresses of the aisles: north to v 24.0 from the west front to
     u -10.8, south to v -24.45 as far as u -14.6 (footprint vertices 0-1 and 14-15). */
  naveChapels: {
    north: { u1: -10.8, outer: 24.0, count: 4 },
    south: { u1: -14.6, outer: -24.45, count: 3 },
    eaveY: 10,
    pitch: 60 * DEG,
  },
  /* The transverse chapels of 1485-1517 on the line of the transept, split at their buttresses
     (footprint vertices 16-31 and 41-53; the north-west corner is cut back to u -9.0); those west of
     the arms start at the u given. The arms of St George (north) and St Sebastian (south) stand on the
     crossing bays, never raised (Janse 2004, section 1.11), on their traced walls (vertices 21-28 and
     41-48) with the corner buttresses the trace shows (u from, u to, outer v). */
  transeptChapels: {
    north: [-9.0],
    south: [-14.6, -8.9],
    northOuter: 29.2,
    southOuter: -31.2,
    arms: {
      north: { span: [0.26, 11.4], outer: 29.55, buttresses: [[0.26, 1.27, 30.95], [10.49, 11.4, 30.98]] },
      south: { span: [0.2, 11.62], outer: -31.52, buttresses: [[0.18, 0.98, -32.78], [11.0, 11.71, -32.82]] },
    },
    eaveY: 14,
    pitch: 58 * DEG,
  },
  /* The hall choir with its ambulatory, c.1445-55: bays of 5, 4.4, 4.2 and 4.2 m from the crossing
     to the apse chord, under one roof as the woodcut draws it (oudekerk-woodcut.jpg), with a roof
     turret over its westernmost bay (pre-1545 images). The apse is the traced one (vertices 34-39). */
  choir: {
    bayU0: 11.4,
    bays: [5, 4.4, 4.2, 4.2],
    vS: -12.8,
    vN: 12.4,
    eaveY: 12,
    pitch: 45 * DEG,
    turretRise: 7,
    turretSink: 1,
    apse: [
      [29.2, -12.8],
      [37.1, -10.4],
      [41.4, -4.0],
      [41.5, 3.3],
      [37.0, 10.2],
      [29.2, 12.4],
    ],
  },
  /* The sacristy of c.1516 south of the choir (vertices 32-34), a low range. */
  sacristy: { u0: 11.8, u1: 29.1, v0: -17.9, v1: -12.8, eaveY: 7, pitch: 50 * DEG },
  /* The stair turret against the south aisle's west front, repaired in 1539 (vertices 8-13). */
  stairTurret: { center: [-32.95, -15.4], radius: 2.3, bodyY: 16, tipY: 21 },
  /* The doorways, each open from the ground. The south portal with the library over it was built
     shortly after 1462 (Janse 2004, section 1.9, by search summary). Its place is not recorded here:
     it is set in the transverse chapel beside the arm of St Sebastian, the part of the south side
     that stood first. The door under the tower is the user's supplied account
     (docs/references/USER-NOTES.md), not checked against Janse. Widths, heights and the sill of the
     library window are estimates. The north portal is left out until its date is sourced. */
  portals: {
    tower: { width: 2.2, top: 4.2 },
    south: { width: 2.6, top: 4.6, windowSill: 6.2 },
  },
  /* Built after 1538 and left out: the Lady Chapel north of the choir, 1551-54. */
  absent: { mariakapel: { u0: 11.4, u1: 29.1, v0: 12.4, v1: 24.6 } },
} as const;

export interface Box {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

/** An aisle of the hall nave under its own roof, ridged along the church. */
export interface AislePlan extends Box {
  readonly side: Side;
  readonly eaveY: number;
  readonly ridgeY: number;
}

/** The raised middle vessel: bay boundaries along u, its gables' shoulders and apexes, its lancets. */
export interface LichtbeukPlan {
  readonly bays: readonly number[];
  readonly shoulderY: number;
  readonly ridgeY: number;
  readonly sill: number;
  readonly windowTop: number;
  readonly windowWidth: number;
}

export interface OudeKerkPlan {
  readonly frame: PlanFrame;
  readonly tower: { readonly center: Vec2; readonly half: number; readonly bodyY: number; readonly lanternRadius: number } & Pick<
    typeof OUDE_KERK.tower,
    'lanternFoot' | 'lanternTop' | 'tipY'
  >;
  /** The middle vessel of the hall nave, from the west front to the choir. */
  readonly nave: Box;
  readonly lichtbeuk: LichtbeukPlan;
  /** The two eastern bays of the nave, crossed by the transept at hall height. */
  readonly crossing: { readonly u0: number; readonly u1: number };
  readonly aisles: readonly AislePlan[];
  readonly naveChapels: readonly ChapelPlan[];
  readonly transeptChapels: readonly ChapelPlan[];
  /** The arms of St George (north) and St Sebastian (south), each from the nave's axis to its front. */
  readonly transeptArms: readonly ChapelPlan[];
  readonly choir: {
    readonly u0: number;
    readonly bayU0: number;
    readonly chordU: number;
    readonly vS: number;
    readonly vN: number;
    readonly vc: number;
    readonly eaveY: number;
    readonly ridgeY: number;
    readonly apse: Ring;
    readonly turret: { readonly u: number; readonly footY: number; readonly topY: number };
  };
  readonly sacristy: Box & { readonly eaveY: number; readonly ridgeY: number };
  readonly stairTurret: typeof OUDE_KERK.stairTurret;
}

export function oudeKerkFrame(landmark: Landmark): PlanFrame {
  return planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
}

function chapelRow(side: Side, u0: number, u1: number, count: number, inner: number, outer: number): ChapelPlan[] {
  const { eaveY, pitch } = OUDE_KERK.naveChapels;
  const width = (u1 - u0) / count;
  const ridgeY = eaveY + (width / 2) * Math.tan(pitch);
  return Array.from({ length: count }, (_, k) => ({ side, u0: u0 + k * width, u1: u0 + (k + 1) * width, inner, outer, eaveY, ridgeY }));
}

function transeptChapel(side: Side, u0: number, u1: number, inner: number, outer: number): ChapelPlan {
  const { eaveY, pitch } = OUDE_KERK.transeptChapels;
  return { side, u0, u1, inner, outer, eaveY, ridgeY: eaveY + ((u1 - u0) / 2) * Math.tan(pitch) };
}

/** Chapels between successive starts, the last one ending where the crossing begins. */
function transeptRow(side: Side, starts: readonly number[], end: number, inner: number, outer: number): ChapelPlan[] {
  return starts.map((u0, k) => transeptChapel(side, u0, starts[k + 1] ?? end, inner, outer));
}

function naveOf(eastU: number): { nave: Box; aisles: AislePlan[] } {
  const n = OUDE_KERK.nave;
  const vc = OUDE_KERK.tower.vc;
  const u0 = OUDE_KERK.westU;
  const ridgeY = n.eaveY + (n.aisle / 2) * Math.tan(n.pitch);
  const aisle = (side: Side): AislePlan => {
    const [a, b] = [vc + side * n.half, vc + side * (n.half + n.aisle)];
    return { side, u0, u1: eastU, v0: Math.min(a, b), v1: Math.max(a, b), eaveY: n.eaveY, ridgeY };
  };
  return { nave: { u0, u1: eastU, v0: vc - n.half, v1: vc + n.half }, aisles: [aisle(1), aisle(-1)] };
}

function lichtbeukOf(eastU: number): LichtbeukPlan {
  const n = OUDE_KERK.nave;
  const l = OUDE_KERK.lichtbeuk;
  const bay = (eastU - OUDE_KERK.westU) / n.bays;
  const ridgeY = n.eaveY + n.half * Math.tan(n.pitch) + l.rise;
  const bays = Array.from({ length: l.bays + 1 }, (_, k) => OUDE_KERK.westU + k * bay);
  const shoulderY = ridgeY - (bay / 2) * Math.tan(l.gablePitch);
  const sill = n.eaveY + l.sillOverWalkway;
  return { bays, shoulderY, ridgeY, sill, windowTop: ridgeY - l.window.topBelowRidge, windowWidth: l.window.width };
}

function choirOf(): OudeKerkPlan['choir'] {
  const c = OUDE_KERK.choir;
  const vc = (c.vS + c.vN) / 2;
  const ridgeY = c.eaveY + ((c.vN - c.vS) / 2) * Math.tan(c.pitch);
  const chordU = c.bayU0 + c.bays.reduce((a, b) => a + b, 0);
  const turret = { u: c.bayU0 + c.bays[0] / 2, footY: ridgeY - c.turretSink, topY: ridgeY + c.turretRise };
  const apse: Ring = c.apse.map((p): Vec2 => [p[0], p[1]]);
  return { u0: c.bayU0, bayU0: c.bayU0, chordU, vS: c.vS, vN: c.vN, vc, eaveY: c.eaveY, ridgeY, apse, turret };
}

function towerOf(): OudeKerkPlan['tower'] {
  const t = OUDE_KERK.tower;
  const lanternRadius = t.lanternWidth / 2 / Math.cos(Math.PI / 8);
  const center: Vec2 = [OUDE_KERK.westU + t.overlap - t.side / 2, t.vc];
  return { center, half: t.side / 2, bodyY: t.bodyY, lanternFoot: t.lanternFoot, lanternTop: t.lanternTop, tipY: t.tipY, lanternRadius };
}

export function planOudeKerk(frame: PlanFrame): OudeKerkPlan {
  const choir = choirOf();
  const { nave, aisles } = naveOf(choir.u0);
  const lichtbeuk = lichtbeukOf(choir.u0);
  const crossing = { u0: lichtbeuk.bays[lichtbeuk.bays.length - 1]!, u1: choir.u0 };
  const [north, south] = aisles as [AislePlan, AislePlan];
  const nc = OUDE_KERK.naveChapels;
  const tc = OUDE_KERK.transeptChapels;
  const s = OUDE_KERK.sacristy;
  const vc = OUDE_KERK.tower.vc;
  return {
    frame,
    tower: towerOf(),
    nave,
    lichtbeuk,
    crossing,
    aisles,
    naveChapels: [
      ...chapelRow(1, OUDE_KERK.westU, nc.north.u1, nc.north.count, north.v1, nc.north.outer),
      ...chapelRow(-1, OUDE_KERK.westU, nc.south.u1, nc.south.count, south.v0, nc.south.outer),
    ],
    transeptChapels: [
      ...transeptRow(1, tc.north, tc.arms.north.span[0], north.v1, tc.northOuter),
      ...transeptRow(-1, tc.south, tc.arms.south.span[0], south.v0, tc.southOuter),
    ],
    transeptArms: [
      transeptChapel(1, tc.arms.north.span[0], tc.arms.north.span[1], vc, tc.arms.north.outer),
      transeptChapel(-1, tc.arms.south.span[0], tc.arms.south.span[1], vc, tc.arms.south.outer),
    ],
    choir,
    sacristy: { ...s, ridgeY: s.eaveY + ((s.v1 - s.v0) / 2) * Math.tan(s.pitch) },
    stairTurret: OUDE_KERK.stairTurret,
  };
}
